import { Button, Chip, Divider, Grid, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { useAWSWebsocket } from "../../../Hooks/useAWSWebsocket"
import { useToast } from "../../../Hooks/useToast"
import { useUser } from "../../../Hooks/useUser"
import { applicationApi, useUpdateApplicationMutation } from "../../../Redux/api/applicationApi"
import { useAcceptJobMutation, useUpdateJobMutation } from "../../../Redux/api/jobApi"
import { useAppDispatch, useAppSelector } from "../../../Redux/store"
import { KaziAvatar } from "../../../Shared Components/Kazi/KaziAvatar"
import { IClientApplicationViewApplications } from "../../../Types/Application"
import { NotificationType } from "../../../Types/Notification"
import { formatUserName } from "../../../Utils/String"
import { ClientViewApplicationModal } from "./ClientViewApplicationModal"
import { KaziButton } from "../../../Shared Components/Kazi/KaziButton"
import { useNavigate, useSearchParams } from "react-router-dom"
import { TalentPreview } from "../../../Shared Components/TalentPreview"
import StripeCheckoutDialog from "../../Settings/Components/Membership/StripeCheckoutDialog"
import { usePaymentRequestMutation } from "../../../Redux/api/stripeApi"
import { JobConstants } from "../../../Types/Job"

interface ITalentApplicationProps {
    application: IClientApplicationViewApplications
    hiredTalent?: string;
    denied: boolean;
}
export const ApplicationReview = ({ application, hiredTalent, denied }:ITalentApplicationProps) => {
    /* ******************** Hooks ******************** */
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { showToast } = useToast();
    const { getUsername } = useUser();
    const { sendNotification } = useAWSWebsocket();
    const dispatch = useAppDispatch();
    const [acceptJob] = useAcceptJobMutation();
    const [updateApplication, {data: updateAppResponse, isLoading: updateAppLoading}] = useUpdateApplicationMutation();
    const [requestPayment] = usePaymentRequestMutation();
    /* ******************** Variables ******************** */
    const locator = searchParams.get('application');
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const talent = application.talent;
    const job = useAppSelector( state => state.ApplicationReview.job);
    const hasTalent = (hiredTalent?.length ?? 0) > 0
    const hasQuestions = (application?.application_form?.length ?? 0) > 0
    const [viewApplication, setViewApplication] = useState(false);
    const [clientSecret, setClientSecret] = useState("");
    const itemizedList = useMemo(() => {
        const project = Number(job?.pay_amount ?? 0);
        const serviceFee = Number((project * 0.15).toFixed(2));
        return {
            project,
            serviceFee
        }
    }, [job])
    /* ******************** Functions ******************** */
    const onComplete = () => {
        if (job) {
            const talent_locator = application.talent_locator
            acceptJob({
                locator: job.locator,
                body: {
                    talent_locator
                }
            })
            .unwrap()
            .then(res => {
                // sendNotification({
                //     type: NotificationType.ApplicationAccepted,
                //     receiver_locator: talent_locator,
                //     navigation_locator: job.locator,
                //     notification: `${getUsername()} has accepted your job application`
                // })
                if (viewApplication) {
                    setViewApplication(false);
                }
                dispatch(applicationApi.util.invalidateTags(['ApplicationByJob']));
                navigate('/messages')
            })
            .catch(err => {
                console.log(err)
                showToast({
                    text: 'Unable to accept application',
                    variant: 'error'
                })
            })
        }
    }
    const handleAccept = () => {
        if (job) {
            if (job.type === JobConstants.JobType.ShortTerm) {
                requestPayment({
                    project_price: Number((itemizedList.project + itemizedList.serviceFee).toString().replaceAll('.', '')) * 100,
                    project_name: job.title,
                    talent_locator: application.talent_locator,  
                    client_locator: job.client_locator
                })
                .unwrap()
                .then( res => {
                    setClientSecret(res.clientSecret);
                    setCheckoutOpen(true)
                })
                .catch(err => {
                    console.error("Error occured while getting client secret: ", err);
                    showToast({
                        text: "Unable to accept",
                        variant: 'error'
                    })
                });
            } else {
                onComplete();
            }
        }
    }
    const handleDecline = () => {
        if (job) {
            updateApplication({
                locator: application.locator,
                updated_data: {
                    status: 'denied'
                }
            })
            .unwrap()
            .then(res => {
                if (viewApplication) {
                    setViewApplication(false);
                }
                dispatch(applicationApi.util.invalidateTags(['ApplicationByJob']))
            })
            .catch(err => {
                console.log(err)
                showToast({
                    text: 'Unable to decline application',
                    variant: 'error'
                })
            })
        }
    }
    const handleView = () => {
        hasQuestions ? setViewApplication(true) : ( !locator && navigate(`/profile/talent/${talent?.locator}`));
        setSearchParams({
            application: application.locator
        })
    }
    /* ******************** Effects ******************** */
    useEffect(() => {
        if (locator && locator === application.locator) {
            handleView();
        }
    }, []);
    /* ******************** JSX ******************** */
    return (
        <>
            <Grid item xs display={'flex'} flexDirection={'row'} >
                <TalentPreview 
                    locator={talent?.locator}
                    first_name={talent?.first_name}
                    last_name={talent?.last_name}
                    title={talent?.title}
                />
                <Grid flex={1} justifyContent={'flex-end'} display={'flex'} flexDirection={'row'} gap={'16px'} alignItems={'center'}>
                    {/**TODO: Add logic to show new applications*/}
                    {
                        hasTalent && hiredTalent === application.talent_locator && 
                        <Chip label={'ACCEPTED'} size={'small'} sx={{backgroundColor: 'green.50', color: 'green.300'}} />
                    }
                    { denied && <Chip label={'DECLINED'} size={'small'} sx={{backgroundColor: '#e51c1c', color: '#FFF'}} />}
                    <Button variant='text' sx={{ fontWeight: 'bold'}} onClick={handleView}>{hasQuestions ? 'View application' : 'View profile'}</Button>
                    <Button variant='outlined' sx={{ fontWeight: 'bold'}} onClick={handleDecline} disabled={hasTalent || denied} >Decline</Button>
                    <KaziButton 
                        variant='contained' 
                        disabled={hasTalent || denied} 
                        onClick={handleAccept}
                        text="Accept"
                    />
                </Grid>
            </Grid> 
            <Divider />
            <ClientViewApplicationModal 
                open={viewApplication} 
                setOpen={setViewApplication} 
                talentInfo={application.talent}
                form={application.application_form} 
                handleAccept={handleAccept}
                handleDecline={handleDecline}
                isAccepted={hasTalent}
            />
            {clientSecret.length > 0 && <StripeCheckoutDialog 
                open={checkoutOpen}
                setOpen={setCheckoutOpen}
                clientSecret={clientSecret}
                onComplete={onComplete}
                itemizedList={itemizedList}
            />}
        </>
    )
}